import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { useAuthStore } from '../../../stores/auth-store.js';
import { login, loginTotp } from '../api.js';

type Stage = 'credentials' | 'totp';

/** Logo MetricsFarma (gráfico de linha) — mesmo SVG do app web-v2. */
function MetricsFarmaMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M3 17l4-8 4 4 4-6 4 5"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 21h18" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Ícones inline — idênticos ao web-v2 (não usar lucide: desenho difere). */
const MailIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const LockIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const EyeOffIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const EyeIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/** Bloco de erro — ícone + texto, igual web-v2. */
function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ef4444"
        strokeWidth="2"
        className="shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p className="text-sm text-red-700">{message}</p>
    </div>
  );
}

function msgFromErr(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.problem.detail || err.problem.title || fallback;
  return fallback;
}

export function LoginPage() {
  const [stage, setStage] = useState<Stage>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);
  const setPendingToken = useAuthStore((s) => s.setPendingToken);

  const stage1 = useMutation({
    mutationFn: async () => login(email, password),
    onSuccess: (data) => {
      if (data.status === 'totp_setup_required') {
        setPendingToken(data.setup_token, 'totp_setup');
        void navigate({ to: '/setup-totp' });
      } else {
        setChallengeToken(data.challenge_token);
        setStage('totp');
      }
    },
    onError: (err) => setErro(msgFromErr(err, 'Email ou senha inválidos.')),
  });

  const stage2 = useMutation({
    mutationFn: async () => loginTotp(challengeToken, code),
    onSuccess: (data) => {
      setUser(data.user);
      void navigate({ to: '/' });
    },
    onError: (err) => setErro(msgFromErr(err, 'Código inválido ou expirado.')),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setErro(null);
    if (stage === 'credentials') stage1.mutate();
    else stage2.mutate();
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
      <div className="w-full max-w-[400px]">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-[#0a4ee3] flex items-center justify-center mb-3 shadow-lg">
            <MetricsFarmaMark size={24} />
          </div>
          <h1 className="text-xl font-bold text-[#0f172a] tracking-tight">MetricsFarma</h1>
          <p className="text-sm text-[#64748b] mt-1">Inteligência de vendas para farmácias</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-8">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-[#0f172a]">
              {stage === 'credentials' ? 'Entrar na plataforma' : 'Verificação em duas etapas'}
            </h2>
            <p className="text-sm text-[#64748b] mt-1">
              {stage === 'credentials'
                ? 'Use suas credenciais para acessar'
                : 'Confirme sua identidade para continuar'}
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            {stage === 'credentials' ? (
              <>
                <Input
                  label="E-mail"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                  autoFocus
                  leftIcon={MailIcon}
                />

                <Input
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  leftIcon={LockIcon}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-[#94a3b8] hover:text-[#64748b] transition-colors"
                      title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      {showPassword ? EyeOffIcon : EyeIcon}
                    </button>
                  }
                />

                {erro && <ErrorBanner message={erro} />}

                <Button type="submit" loading={stage1.isPending} size="lg" className="w-full mt-1">
                  Entrar
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#d97706"
                    strokeWidth="2"
                    className="shrink-0 mt-0.5"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    Abra seu app autenticador e digite o código de 6 dígitos.
                  </p>
                </div>

                <Input
                  label="Código TOTP"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="\d{6}"
                  autoComplete="one-time-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  className="text-center font-mono text-[18px] tracking-[0.5em]"
                />

                {erro && <ErrorBanner message={erro} />}

                <Button type="submit" loading={stage2.isPending} size="lg" className="w-full mt-1">
                  Verificar
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setStage('credentials');
                    setCode('');
                    setChallengeToken('');
                    setErro(null);
                  }}
                  className="text-xs font-medium text-[#64748b] hover:text-[#0f172a] transition-colors"
                >
                  ← Voltar
                </button>
              </>
            )}
          </form>
        </div>

        <p className="text-center text-xs text-[#94a3b8] mt-6">
          © {new Date().getFullYear()} MetricsFarma · Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
